import {
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  OnInit,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AbookDialogService } from '@app/shared/components/abook-dialog/abook-dialog.service';

// Material
import { MatMenuModule } from '@angular/material/menu';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

// core
import { DateService } from '@core/services/date.service';

// shared
import { Booking } from '@shared/models/booking';
import { AbookButton } from '@shared/components/abook-button/abook-button';
import { SideMenusService } from '@shared/components/side-menus/side-menus.service';
import { BookingRequest } from '@shared/models/models';
import { defaultPaymentTypeFor, PaymentType } from '@shared/models/paymentType';
import { BookingTypeName } from '@shared/models/enums';
import { AddBookingPeriodFailure, CheckoutStore } from '@features/checkout/checkout.store';
import { CheckoutService } from '@features/checkout/checkout.service';
import { JourneyStore } from '@features/checkout/journey.store';

// feature
import { AssociationsStore } from '../../store/associations.store';
import { SearchStore } from '../../store/search.store';
import { Association } from '../../models/association';
import { TimeslotCheckAvailabilityResponse } from '../../models/timeslot-check-availability-reponse';
import { DateIconLabelVariationsComponent } from '@features/checkout';

import { id_SearchResponseTimeslot_Convention, SearchResponseTimeslot } from '../../models/search-response';
import { BasketRepeat, BasketRepeatableService } from './services/basket-repeatable.service';
import { RepeatedBookingComponent } from '@features/booking/calender/templates/create-booking-modal/components/repeated-booking/repeated-booking.component';
import { BookingPeriodExtended } from '@features/booking/calender/model/models';
import { CalenderService } from '@features/booking/calender/calender.service';

// internals
import { BasketStore } from './basket.store';
import { BasketItem, CombinedBasketItem } from './basket';
import { AbookSnackbarService } from '@app/shared/components/abook-snackbar/abook-snackbar.service';
import { blockingBasketMaxBookingTime } from './max-booking-time';

import {
  BookingPeriodDetails,
  IconLabelComponent,
  CheckoutCardComponent,
  CheckoutCardHeaderComponent,
} from '@features/checkout';
import { errorMessage } from '@shared/utilities/error-message';
import { DanishDatePipe } from '@shared/utilities/danish-date.pipe';
import { CapitalizeFirstPipe } from '@shared/utilities/capitalize-first.pipe';
import { basketSeries } from './basket-series';

@Component({
  selector: 'abook-basket',
  imports: [
    MatBadgeModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    CheckoutCardComponent,
    CheckoutCardHeaderComponent,
    BookingPeriodDetails,
    IconLabelComponent,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    AbookButton,
    DateIconLabelVariationsComponent,
    CurrencyPipe,
    RepeatedBookingComponent,
    DanishDatePipe,
    CapitalizeFirstPipe,
  ],
  // The Gentag view's RepeatedBookingComponent checks each date with these.
  providers: [CalenderService],
  templateUrl: './basket.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './basket.component.scss',
})
export class BasketComponent implements OnInit {
  readonly basketStore = inject(BasketStore);
  readonly associationsStore = inject(AssociationsStore);
  readonly searchStore = inject(SearchStore);
  readonly checkoutStore = inject(CheckoutStore);
  readonly journeyStore = inject(JourneyStore);
  readonly checkoutService = inject(CheckoutService);
  readonly sideMenusService = inject(SideMenusService);
  readonly router = inject(Router);
  readonly basketRepeatableService = inject(BasketRepeatableService);
  private readonly dialogService = inject(AbookDialogService);
  private readonly dateService = inject(DateService);
  private readonly snackbarService = inject(AbookSnackbarService);

  /**
   * The basket's lines as series (#1138): the same activity, facilitet, time of day and type on
   * several dates — a repeat — make one card with the dates listed; any other line its own card.
   */
  readonly basketSeries = computed(() => basketSeries(this.basketStore.combinedBasketItems()));

  /** The series whose dates are folded open, by key. */
  protected readonly openSeries = signal(new Set<string>());

  toggleSeries(key: string) {
    this.openSeries.update((open) => {
      const next = new Set(open);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }

  /** A series' card emptied at once, with one "Fortryd" for all its dates. */
  removeSeries(items: CombinedBasketItem[]) {
    return this.remove(items, `${items.length} datoer er fjernet fra kurven`);
  }

  removeItem(timeslot: CombinedBasketItem) {
    return this.remove([timeslot], 'Tiden er fjernet fra kurven');
  }

  /** Lines out of the basket, the rest checked again, with one "Fortryd" for them all. */
  private async remove(items: CombinedBasketItem[], undoTitle: string) {
    const ids = new Set(items.map((item) => item.id));
    try {
      await this.validateBasketData(
        this.lastCheckAvailabilityResponse().filter((x) => !ids.has(x._basketItemId)),
      );
      this.checkoutError.set(null);
    } catch (error) {
      this.checkoutError.set(errorMessage(error, 'Kunne ikke tjekke, om tiderne er ledige'));
    }

    const removed = items
      .flatMap((item) => item.combinedTimeslotIds)
      .map((id) => this.basketStore.basketItemEntityMap()[id])
      .filter((item): item is BasketItem => !!item);
    items.forEach((item) => this.basketStore.removeBasketItem(item));
    this.offerUndo(undoTitle, removed);
  }

  /** Empty the whole basket after the user confirms. */
  async confirmClearBasket(): Promise<void> {
    const confirmed = await this.dialogService.confirm({
      title: 'Tøm kurv',
      message: 'Er du sikker på, at du vil fjerne alle tider fra kurven?',
      confirmText: 'Tøm kurv',
    });
    if (confirmed) {
      const removed = this.basketItemsNow();
      this.basketStore.clearBasket();
      this.offerUndo('Kurven er tømt', removed);
    }
  }

  basketIsOpen = false;
  association: Association;

  readonly location = computed(() => this.basketStore.location());
  /** The basket holds a request — times that must be approved before they are booked. */
  readonly isRequest = computed(() => this.basketStore.currentAvailabilityType() === 'REQUEST');
  readonly userAssociation = computed(() => this.associationsStore.selectedEntity());
  private readonly userAssociationId = computed(() => this.userAssociation()?.userId);
  readonly lastCheckAvailabilityResponse = signal<TimeslotCheckAvailabilityResponse[]>([]);

  /** Identity of the basket periods by time — changes only on a real add/remove/edit,
   *  not on a price/availability refresh. */
  private readonly basketPeriodsKey = computed(() =>
    this.basketStore
      .combinedBasketItems()
      .map((i) => `${i.id}:${i.start}:${i.end}`)
      .join('|'),
  );

  /** Resets to null whenever the basket's periods actually change (a time is corrected, or
   *  an item added/removed), so a fixed problem no longer shows the old message. Keyed on
   *  id+start+end so the availability/price refresh that runs during checkout() itself does
   *  not wipe a freshly-set error — the next "Videre" re-validates. Picking another association
   *  resets it too (keyed on its id, not the object), so "Checkout skal gøres på vegne af en
   *  forening" goes once one is chosen. A manual .set() persists until either changes. */
  readonly checkoutError = linkedSignal<string | null>(() => {
    this.basketPeriodsKey();
    this.userAssociationId();
    return null;
  });

  ngOnInit(): void {
    this.checkoutStore.setRequestFulfilled();
  }

  /** How many lines the basket had last time — to see it go from something to empty. */
  private lastCount = this.basketStore.itemsCount();

  /**
   * Emptied — the last line removed or "Tøm kurv" — the sheet folds away by itself; "Fortryd" stays
   * in the snackbar, and the basket bar comes back with what it restores.
   */
  private readonly closeWhenEmptied = effect(() => {
    const count = this.basketStore.itemsCount();
    if (this.lastCount > 0 && count === 0) this.sideMenusService.toggleAltMenuSubject.next('close');
    this.lastCount = count;
  });

  private basketItemsNow(): BasketItem[] {
    const map = this.basketStore.basketItemEntityMap();
    return this.basketStore.basketItemIds().map((id) => map[id]);
  }

  /** "Fortryd" for 5 seconds after a removal (design 3a, the design system's snackbar). */
  private offerUndo(title: string, removed: BasketItem[]) {
    if (!removed.length) return;
    this.snackbarService.showCustom(
      {
        title,
        message: '',
        actionOneText: 'Fortryd',
        actionOne: () => this.basketStore.restoreBasketItems(removed),
      },
      { duration: 5000 },
    );
  }

  toggleBasket() {
    this.basketIsOpen = !this.basketIsOpen;
  }

  getItemResourceName(item: CombinedBasketItem) {
    // Read resource names directly from basket item (stored at add time)
    let str = item.resourceName || '';

    if (item.resourceParentName) {
      str = item.resourceParentName + ' / ' + str;
    }

    return str;
  }

  validateAssociation(userAssociation: Association): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!userAssociation) {
        reject(new Error('Checkout skal gøres på vegne af en forening'));
      }

      if (!userAssociation.userId) {
        reject(new Error('Checkout skal gøres på vegne af en forening (mangler ID)'));
      }

      resolve('Association validation success');
    });
  }

  validateBasketItems(res: TimeslotCheckAvailabilityResponse[]): Promise<string> {
    return new Promise((resolve, reject) => {
      if (res.some((x) => x.price == null)) {
        reject('Nogle af perioderne har ikke en tilgængelig pris');
      }

      if (res.some((x) => !x.isAvailable)) {
        // One or more of the periods are no longer Available
        reject(new Error('Nogle af perioderne er ikke længere tilgængelige'));
      }

      resolve('basket Items has validation success');
    });
  }

  /**
   * Block checkout when a booked period is shorter than its facility's minimum booking
   * length. Reglen står på prisperioden og kommer med på søgesvarets tid, som kurvlinjen bærer
   * videre. Rejects with a plain string so the existing checkout alert renders the message.
   */
  validateMinBookingLength(): Promise<string> {
    return new Promise((resolve, reject) => {
      for (const item of this.basketStore.combinedBasketItems()) {
        const min = item.minBookingMinutes;
        if (!min) continue;
        const minutes = Math.round(
          (this.dateService.fromApiString(item.end).getTime() - this.dateService.fromApiString(item.start).getTime()) / 60000,
        );
        if (minutes < min) {
          reject(`Minimum bookingtid er ${min} minutter — vælg mindst ${min} minutter for at fortsætte.`);
          return;
        }
      }
      resolve('min booking length ok');
    });
  }

  async validateBasketData(res: TimeslotCheckAvailabilityResponse[]) {
    return Promise.all([
      this.validateAssociation(this.userAssociation()),
      this.validateBasketItems(res),
      this.validateMinBookingLength(),
      this.validateMaxBookingTime(res),
    ]);
  }

  /**
   * Stop kurven på en maksimumtid, serveren vil afvise — kurvens linjer lagt sammen pr. kvote, ikke
   * hver for sig. Uden dette blev tiderne først afvist ved oprettelsen af bookingperioderne, efter
   * flowet var navigeret videre, så begrundelsen landede på Tilvalg-siden i stedet for i kurven ved
   * kalenderen, hvor tiderne kan rettes.
   */
  validateMaxBookingTime(res: TimeslotCheckAvailabilityResponse[]): Promise<string> {
    return new Promise((resolve, reject) => {
      const blocking = blockingBasketMaxBookingTime(res, this.dateService);

      if (blocking) {
        reject(blocking);
        return;
      }

      resolve('max booking time ok');
    });
  }

  async checkout() {
    this.checkoutError.set(null);

    // Must run before setRequestPending, or the spinner never stops (#1049).
    if (!this.userAssociation()?.userId) {
      this.checkoutError.set('Checkout skal gøres på vegne af en forening');
      return;
    }

    this.checkoutStore.setRequestPending();
    try {
      const res = await firstValueFrom(
        this.basketStore.checkAvailability(
          this.basketStore.combinedBasketItems(),
          this.userAssociation().userId,
          this.checkoutStore.bookingId(),
        ),
      );

      this.lastCheckAvailabilityResponse.set(res);

      await this.validateBasketData(res);
    } catch (error) {
      this.checkoutError.set(errorMessage(error, 'Kunne ikke tjekke, om tiderne er ledige'));
    } finally {
      this.checkoutStore.setRequestFulfilled();
    }

    if (this.checkoutError()) return;

    /** GUIDE : add periods to Booking
     * 1. API create bookingRequest (return booking ID)
     * 2. iterate over basketItems
     * 3. convert basket timeslot to bookingPeriod
     * 4. add bookingPeriod to booking
     * 5. API addPeriodsToBooking(bookingPeriods: BookingPeriod[]) from bookingService
     *
     */

    const bookingRequest: BookingRequest = {
      userId: this.userAssociation().userId, // UserId from selected association
      // Invoiced, as the counter (create-booking-modal) books an association — see defaultPaymentTypeFor.
      paymentType: defaultPaymentTypeFor(
        this.associationsStore.selectedOrganisationTypeId(),
        PaymentType.InvoicePostPayment,
      ),
      note: null,
      externalNote: null,
      pspElement: null,
      responsible: null,
      paymentDue: null,
    };

    // If booking exist, delete it and create a new one
    if (this.checkoutStore.bookingId()) {
      await firstValueFrom(this.checkoutStore.clearBooking(this.checkoutStore.bookingId()));
    }

    const booking = (await firstValueFrom(this.checkoutService.createBooking(bookingRequest))) as Booking;

    if (!booking.id) this.checkoutError.set('Fejl i oprettelse af booking');

    this.checkoutStore.setBookingId(booking.id);
    try {
      const results = await firstValueFrom(
        this.checkoutStore
          .addBookingPeriods(
            booking.id,
            this.basketStore.combinedBasketItems(),
            this.userAssociation()?.name,
            // An organisation (company, internal) books Booking, not the association types (#950).
            this.associationsStore.selectedIsOrganisation() ? BookingTypeName.BOOKING : undefined,
          )
          .pipe(
            catchError((message: string) => {
              // Display error message
              this.checkoutError.set(`Der opstod en fejl i oprettelsen af booking perioder (${message})`);

              return of(null); // Handle error, possibly return a null or error indicator
            }),
          ),
      );

      // En periode API'et afviste må ikke sende brugeren videre: hver periode fanger sin egen fejl,
      // så flowet navigerede til Tilvalg som om alt var gemt, og API'ets begrundelse dukkede op dér.
      const rejected = (results as AddBookingPeriodFailure[] | null)?.find(
        (x) => !!x?.addBookingPeriodError,
      );
      if (rejected) {
        this.checkoutError.set(rejected.addBookingPeriodError);
      }

      this.checkoutStore.setRequestFulfilled();

      if (this.checkoutError()) {
        console.warn('Error in checkout?');
        return;
      }

      /**
       * Navigate to Checkout flow
       * Capture search params before navigating, and initialize checkout journey based on bookingType
       */

      // Capture current search params for "return to search" functionality
      const searchParams = this.searchStore.getSearchQueryParams();

      // copy search params to journey store  (used for "return to search" from checkout complete page)
      this.journeyStore.setLastSearchParams(searchParams);

      const journeyFirstPage = this.journeyStore.journeyConfig().page[0];

      if (!journeyFirstPage) {
        console.warn('No journey pages found in configuration');
        return;
      }

      this.sideMenusService.toggleAltMenuSubject.next('close');
      this.router.navigate(['/checkout', journeyFirstPage.pageRoute]);
    } catch (error) {
      this.checkoutError.set('Der opstod en fejl ved oprettelse af booking');
      this.checkoutStore.setRequestFulfilled();
      console.error('Error adding booking periods:', error);
    }
  }

  /**
   * "Gentag periode" (#1138): the side sheet turns to a Gentag view for the item — no dialog over
   * the page, so the calendar stays in sight beside it. "Tilbage" or adding the dates returns.
   */
  readonly repeating = signal<BasketRepeat | null>(null);

  /** The sheet closed: back to the basket, so no Gentag panel is left open without its sheet. */
  private readonly leaveRepeatWhenClosed = effect(() => {
    if (!this.sideMenusService.altMenuOpened()) untracked(() => this.repeating.set(null));
  });

  onRepeatBasketItem(basketItem: CombinedBasketItem) {
    if (!this.userAssociation()?.userId) {
      this.checkoutError.set('Der skal vælges en forening før gentagne bookinger kan oprettes');
      return;
    }
    this.repeating.set(
      this.basketRepeatableService.repeatOf(
        basketItem,
        this.basketStore.combinedBasketItems(),
        this.checkoutStore.bookingId(),
        this.basketStore.getActivity(basketItem.activityId),
        this.searchStore.locationResources().find((r) => r.id === basketItem.resourceId),
      ),
    );
  }

  onRepeatChosen(periods: BookingPeriodExtended[]) {
    const repeat = this.repeating();
    this.repeating.set(null);
    if (repeat) this.onRepeatedPeriodsSelected(this.basketRepeatableService.toTimeslots(periods, repeat.item));
  }

  /**
   * Handle selected repeated periods and add them to basket
   */
  private onRepeatedPeriodsSelected(timeslots: SearchResponseTimeslot[]): void {
    if (!timeslots?.length) return;

    this.checkoutError.set(null);

    // Convert and add all periods directly to basket
    timeslots.forEach((timeslot) => {
      // Lookup resource data from searchStore for display purposes
      const resource = this.searchStore.locationResources()?.find((r) => r.id === timeslot.resourceId);

      timeslot.locationId = this.location()?.id || null; // Set locationId for the new timeslot
      timeslot.id = id_SearchResponseTimeslot_Convention(
        this.location()?.id || 0,
        timeslot.resourceId,
        timeslot.start,
        timeslot.end,
      ); // Generate unique ID for the new timeslot based on its properties

      this.basketStore.addPrevalidatedTimeslot(timeslot, {
        name: resource?.name,
        parentName: resource?.parent?.name,
      });
    });
  }
}
